/**
 * S 域（投资）**端侧文案** —— 中文名与提示语的唯一出处（批 `99` B2）
 *
 * 依据：`投资域PRD_持仓与买卖` **v2** §4（产品形态）· §5（品种 × 动作矩阵）·
 * §6（口径细则）· `DR-059` D1/D3/D4/D6 · 铁律 8（红涨绿跌）· 铁律 103（中文名归端侧）。
 *
 * ## 🔴 为什么文案单独一个文件
 *
 * 1. **品种中文名不许出现在 `shared` / `server` 字面量里**（铁律 103）——
 *    `nucleus/investment.ts` 只给 `stock` / `fund` / … 六个枚举值，中文在这里；
 * 2. 本域的口径提示（不计收支 / 历史数据 39 分 / 年化样本不足）**必须原样来自 PRD**，
 *    散在四个对话框里必然改一处漏三处；
 * 3. 「动作名字随品种变」是本域最容易被写散的一条（PRD §5）：六类 × 两个方向 = 12 个词，
 *    放在一个 `Record` 里才能一眼看出有没有漏。
 *
 * ## ⚠️ 本文件**无逻辑**
 *
 * 只有字符串常量与 `Record` 映射，不导函数 —— "按状态挑文案"的判断都在
 * `features/investment.ts`（便于单测钉住，例如 `other` 品种没有分红入口）。
 */

// ─────────────────────────── 品种 ───────────────────────────

/**
 * 六个品种的中文名（键 = `nucleus/investment.ts` 的 `HoldingKind` 值）。
 *
 * 🔴 原在 `ui/HoldingsList.tsx`（批 95），B2 迁到这里并**删掉原处** ——
 * 两个 `KIND_LABEL` 并存时，加一个品种只会改到其中一个（铁律 24）。
 */
export const KIND_LABEL: Record<string, string> = {
  stock: '股票',
  fund: '基金',
  bond: '债券',
  wealth: '理财',
  deposit: '存款',
  other: '其他',
};

/** 台账里的品种顺序（股 → 基 → 债 → 理财 → 存款 → 其他）；不在表里的排最后 */
export const KIND_ORDER: readonly string[] = ['stock', 'fund', 'bond', 'wealth', 'deposit', 'other'];

/**
 * **品种 × 动作矩阵**（PRD §5 原表）。
 *
 * | 品种 | 买入叫 | 退出叫 | 收益叫 | 收益走哪个 `action` |
 * |---|---|---|---|---|
 * | 股票 | 买入 | 卖出 | 现金分红 | `dividend` |
 * | 基金 | 申购 | 赎回 | 现金分红 | `dividend` |
 * | 债券 | 买入 | 卖出 | 利息 | `interest` |
 * | 理财 | 申购 | 赎回 | 到期结息 | `interest` |
 * | 存款 | 存入 | 取出 | 结息 | `interest` |
 * | 其他 | 买入 | 卖出 | ——（`null` = 不给入口） | —— |
 *
 * 🔴 **只有一套实现**：无论这些词怎么变，落库都是 `(quantityE4, priceE4)` + `action`
 * （PRD §5 末段 / `DR-059` §12.1）。本表只决定界面文案，不产生第二套算法。
 */
export const ACTION_OF_KIND: Record<
  string,
  { buy: string; sell: string; income: string; incomeKind: 'dividend' | 'interest' | null }
> = {
  stock: { buy: '买入', sell: '卖出', income: '现金分红', incomeKind: 'dividend' },
  fund: { buy: '申购', sell: '赎回', income: '现金分红', incomeKind: 'dividend' },
  bond: { buy: '买入', sell: '卖出', income: '利息', incomeKind: 'interest' },
  wealth: { buy: '申购', sell: '赎回', income: '到期结息', incomeKind: 'interest' },
  deposit: { buy: '存入', sell: '取出', income: '结息', incomeKind: 'interest' },
  other: { buy: '买入', sell: '卖出', income: '记收益', incomeKind: null },
};

/** 品种缺省（`kind` 是库里读出来的字符串，可能有历史脏值）⇒ 按「其他」处理 */
export const ACTION_FALLBACK = ACTION_OF_KIND.other!;

// ─────────────────────────── 页面 ───────────────────────────

export const PAGE_TITLE = '投资';
export const PAGE_SUBTITLE = '持仓台账 + 买卖赎回 + 回报率 · 账户页只留「还没买入的现金」';
export const PAGE_BUY = '买入';

/** 汇总卡 */
export const SUMMARY_TITLE = '回报率汇总';
export const SUMMARY_INVESTED = '总投入';
export const SUMMARY_MARKET_VALUE = '当前市值';
export const SUMMARY_REALIZED = '已实现盈亏';
export const SUMMARY_DIVIDEND = '累计分红';
export const SUMMARY_UNREALIZED = '浮盈';
export const SUMMARY_TOTAL_RETURN = '总收益率';
export const SUMMARY_REALIZED_RETURN = '已实现收益率';
export const SUMMARY_HOLDING_COST = '持仓成本';
export const SUMMARY_PROCEEDS = '卖出回款';
export const SUMMARY_XIRR = '年化';

/**
 * 🔴 年化的口径提示（`DR-059` D4 / `AC-INV12`）—— **必须与数字同屏**。
 *
 * 本项目真实投资流水只覆盖 80 天，年化会算出 162%：数学正确、直观误导。
 * 所以不给"隐藏年化"这条退路（那违背负责人 D4 的决策），而是让数字带着原因出现。
 *
 * ⚠️ 拆成前缀/后缀两个常量（本文件不出函数）：拼接在 `features/investment.ts` 的
 * `xirrHintOf()` 里，天数由调用方传。
 */
export const XIRR_HINT_PREFIX = '持有 ';
export const XIRR_HINT_SUFFIX = ' 天 · 样本不足 1 年，仅供参考';

/** 年化算不出来时的文本（🔴 `null` 不是 0% —— `AC-INV12`） */
export const XIRR_NONE_TEXT = '算不出（现金流不足 2 笔或无法收敛）';

/**
 * 历史数据口径差提示（`DR-059` D7 / `AC-INV9`）。
 *
 * 实测 2026-10-24：已实现 + 分红 + 浮盈 ¥194,890.15 vs 闭合式 ¥194,890.16 ⇒ 差 39 分。
 * 不修正、不摊平，**页首如实标注** —— 把差藏起来比差 39 分更糟。
 */
export const CLOSING_DIFF_HINT = '历史数据存在口径差（v1 迁移产生），不影响新增交易';

/** 台账 */
export const LEDGER_TITLE = '持仓台账';
export const LEDGER_HINT = '按平台分组 · 成本口径为移动加权平均 · 手续费不进成本';
export const CLEARED_TEXT = '已清仓';
export const MATURITY_UNFILLED_TEXT = '到期日未填';
export const EMPTY_HOLDINGS_TEXT =
  '还没有持仓。点右上角「买入」新建 —— 股票 / 基金 / 债券 / 理财 / 存款都从这里进。';
export const UNASSIGNED_PLATFORM = '未指定平台';

/** 台账列名（表头） */
export const COL_NAME = '名称';
export const COL_QUANTITY = '份额';
export const COL_COST_PRICE = '成本价';
export const COL_PRICE = '现价';
export const COL_MARKET_VALUE = '市值';
export const COL_PNL = '浮盈';
export const COL_WEIGHT = '占比';

/** 动作按钮 */
export const BTN_PRICE = '改价';
export const BTN_BATCH_PRICE = '批量改价';
export const BTN_INCOME = '记收益';
export const BTN_SAVE = '保存';
export const BTN_CANCEL = '取消';

// ─────────────────────────── 三个对话框 ───────────────────────────

export const DLG_TRADE_TITLE_BUY = '买入';
export const DLG_TRADE_TITLE_SELL = '卖出';
export const DLG_INCOME_TITLE = '记收益';
export const DLG_PRICE_TITLE = '更新现价';
export const DLG_BATCH_PRICE_TITLE = '批量改价';

export const F_HOLDING = '持仓';
export const F_KIND = '品种';
export const F_NAME = '名称';
export const F_CODE = '代码（选填）';
export const F_QUANTITY = '份额';
export const F_AMOUNT = '金额';
export const F_PRICE = '价格';
export const F_FEE = '手续费（选填）';
export const F_ACCOUNT = '资金账户';
export const F_DATE = '日期';
export const F_NOTE = '备注（选填）';
export const F_MATURITY = '到期日（选填）';
export const F_NEW_PRICE = '新现价';
export const F_INCOME_AMOUNT = '收益金额';

/** 三框联动（`DR-072` D6）：净值 / 份额 / 总价 —— 银行确认单上的三个数 */
export const F_NET_PRICE = '净值（元/份）';
export const F_GROSS = '总价';
export const BTN_FULL_REDEEM = '全部';
export const HINT_TRADE_LINK = '任填两项，第三项自动算；最后改的那个为准（净值和份额以银行确认单为准）';
export const HINT_GROSS = '到账 / 实付金额，以银行确认单为准 —— 与「份额 × 净值」差几分也照记';
export const HINT_PRICE_DERIVED = '净值由「总价 ÷ 份额」反算 —— 份额越大误差越大（一万份可达 0.5 元）';
export const HINT_CLAMPED = '按总价算出的份额超过持仓，已改成全部赎回（总价同步按持仓 × 净值重算）';
export const HINT_TAIL_DIFF = '总价与「份额 × 净值」不等 —— 按你填的总价记账（与银行对账单一致）';

/** 🔴 资金账户必须是投资类（`AC-INV1`）—— 端侧只给候选，校验仍在服务端 */
export const HINT_ACCOUNT = '只能用「投资」类账户，且币种须与持仓一致';
export const HINT_BUY = '资金腿不计收支（买入不是支出）· 成本按移动加权平均重算';
export const HINT_SELL =
  '回款拆两条腿：本金（不计收支）+ 盈亏（计入收支）· 合计恰等于回款 —— 不会让钱凭空多出来';
export const HINT_FEE = '手续费单独记一笔支出（`其他费用` 分类），不进持仓成本';
export const HINT_INCOME = '只记收益，不动持仓数量与成本（分红 ≠ 卖出）· 计入投资收益';
export const HINT_PRICE = '改价只影响市值与回报率；数量、成本价、已实现盈亏都不变';
export const HINT_BATCH_PRICE = '只填要改的；留空表示这一条不改';

/** 金额制品种：录入的是"多少钱"，实际份额由服务端/内核折算（PRD §5） */
export const HINT_AMOUNT_MODE = '这类品种按「金额」录入，份额与到账金额由内核折算';
export const AMOUNT_MODE_UNIT_LABEL = '金额';
export const QTY_MODE_UNIT_LABEL = '份额';

/** 卖出预览 */
export const PREVIEW_PROCEEDS = '预计回款';
export const PREVIEW_COST = '对应成本';
export const PREVIEW_PNL = '预计盈亏';
export const PREVIEW_ACTUAL = '实际到账';
export const HINT_ACTUAL_DIFFERS = '除不尽 —— 到账金额按份额 × 现价折算，与输入可能差几分';

// ─────────────── 交易记录与撤销（批 127 · `DR-073`） ───────────────

export const BTN_HISTORY = '记录';
export const DLG_TRADES_TITLE = '交易记录';
export const BTN_REVOKE = '撤销';
export const EMPTY_TRADES = '还没有交易记录';
/** 🔴 栈式撤销：只能从最后一笔往回撤（撤中间那笔会让后面卖出的成本对不上） */
export const HINT_TRADES = '只能撤销最新的一笔 —— 撤销后持仓与账户余额一起回滚';
export const HINT_REVOKE_WHY = '记录填错时用「撤销」把这一笔整体收回，再重新录一次';
export const COL_TRADE_DATE = '日期';
export const COL_TRADE_ACTION = '动作';
export const COL_TRADE_GROSS = '金额';
export const COL_TRADE_PNL = '盈亏';

// ─────────────────────────── 口径对账（批 99 B3） ───────────────────────────

export const RECONCILE_TITLE = '口径对账';
export const RECONCILE_HINT = '报表按分类聚合 vs 投资记录的已实现盈亏 —— 两条独立路径各算一遍，逐分比对';

/** 列名 */
export const RECONCILE_REPORT_SIDE = '报表';
export const RECONCILE_DOMAIN_SIDE = '投资记录';
export const RECONCILE_GAIN = '收益';
export const RECONCILE_LOSS = '亏损';

/** 结论 */
export const RECONCILE_OK = '一致';
export const RECONCILE_NOT_COMPARABLE = '不可比';

/**
 * 差额的三种解释（`DR-059` D7 与分类共用事实）。
 *
 * 🔴 页面上**必须**给出解释而不只是差额数字：用户看到"差 ¥7,393.51"只会以为算错了。
 */
export const RECONCILE_NOTE_HISTORY =
  '报表里没有这笔收益 —— 多为迁移前的数据（当时只搬了投资流水，没有生成资金流水，故报表看不见）';
export const RECONCILE_NOTE_SHARED =
  '报表比投资记录多 —— 「投资收益」这个分类也用于重大资产的收益，属分类共用，不是错账';
export const RECONCILE_NOTE_UNKNOWN =
  '两侧不等 —— 检查是否有人手工记入「投资收益 / 投资理财」分类，或某笔买卖没生成资金腿';
export const RECONCILE_FX_INCOMPLETE = '本币合计不完整：有外币腿缺当日汇率（报表侧按缺汇率处理）';
export const RECONCILE_EMPTY = '还没有任何投资买卖，暂无可对账内容';
